import { useContext, useEffect } from "react";

import { createFileRoute } from "@tanstack/react-router";
import { AuthContext } from "react-oauth2-code-pkce";

import { clearClientAuthState } from "../../auth/utils";
import { LOGOUT_URL } from "../../config/oauth.config";
import { purgeAllChatHistory } from "../../lib/chatHistoryStorage";

export const Route = createFileRoute("/auth/logout")({
  component: LogoutPage,
});

// Application-controlled logout.
// 1. Clears all OAuth tokens and PKCE state manually (NOT via library's logOut()
//    which writes NEW stale state).
// 2. Navigates to the backend logout endpoint, which clears the Django session
//    and redirects onward (post_logout_redirect_uri).
//
// We navigate (rather than fetch) so the browser sends the API-domain session
// cookie and follows the backend's redirect chain. We drive this ourselves
// instead of using oauthConfig.logoutEndpoint because the library stores PKCE
// state before its logout redirect, causing a "Bad authorization state" error
// when the backend redirects back without a code.
function LogoutPage() {
  const { token } = useContext(AuthContext);

  useEffect(() => {
    // Clear ROCP_* tokens and the pending OAuth flow state.
    // CRITICAL: Do NOT call logOut() - it writes NEW stale PKCE state that causes
    // "Bad authorization state" errors in other tabs' fresh logins.
    clearClientAuthState();
    // Clear the rest of sessionStorage (drafts, handoffs) too
    sessionStorage.clear();
    // Persisted ARIES transcripts live in localStorage so they survive a tab
    // close, so nothing above touches them. Purge here rather than only in
    // useAuth.logout: this route is the single funnel for every logout
    // navigation, including the forced one the 401 handler triggers after a
    // server-side revocation. Otherwise the next user to sign in on this
    // origin could resume the previous session's case transcripts.
    purgeAllChatHistory();

    const params = new URLSearchParams({
      post_logout_redirect_uri: window.location.origin + "/",
    });

    if (token) {
      params.set("id_token_hint", token);
    }

    window.location.replace(`${LOGOUT_URL}?${params.toString()}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ textAlign: "center", padding: "2rem" }}>
      <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>Signing out…</p>
    </div>
  );
}
